import {Component, ElementRef, EventEmitter, OnDestroy, OnInit, ViewChild,} from '@angular/core';
import {AppMenuProfileComponent} from './app.menuprofile.component';
import {LayoutService} from './service/app.layout.service';
import {AppMenuComponent} from "./app.menu.component";
import {NgIf} from "@angular/common";
import {IMenu, IMenuItem} from "./layout.model";
import {Router} from "@angular/router";
import {DocumentationService} from "../api/services/documentation.service";
import {SharedService} from "../@admin/services/shared/shared.service";
import {InternalProductEnum} from "../@admin/utils/constant/common.const";

@Component({
  selector: 'app-sidebar',
  templateUrl: './app.sidebar.component.html',
  standalone: true,
  imports: [AppMenuProfileComponent, AppMenuComponent, NgIf,]
})
export class AppSidebarComponent implements OnDestroy, OnInit {
  public static latestMenuList: any[] = [];

  public static getMenuList: EventEmitter<any[]> = new EventEmitter<any[]>();

  timeout: any = null;

  @ViewChild(AppMenuProfileComponent) menuProfile!: AppMenuProfileComponent;

  public menuList: any[] = [];

  productEnum: number = 0;

  @ViewChild('menuContainer') menuContainer!: ElementRef;

  constructor(public layoutService: LayoutService, public el: ElementRef, private router: Router, private _docService: DocumentationService,
              public _sharedService: SharedService) {
  }

  get menuProfilePosition(): string {
    return this.layoutService.config().menuProfilePosition;
  }

  async ngOnInit(): Promise<void> {
    // Get the current route and determine the route name and product enum
    const currentRoute = this.router.url;
    const {routeName, productEnum} = this.getRouteNameAndProductEnum(currentRoute);

    // Initialize and update sidebar if routeName and productEnum are valid
    if (routeName && productEnum !== undefined) {
      this.productEnum = productEnum;
      await this.getInitialiseAndUpdateSideBar(productEnum, routeName, false);
    }

    // Subscribe to changes in the product ID from the SharedService
    this._sharedService.triggerGetGuideGridDataList$.subscribe(async (productId: number) => {
      const routeName = `documentations-portal/internal/view/${productId}`;
      await this.getInitialiseAndUpdateSideBar(productId, routeName, true);
    });
  }

  async getInitialiseAndUpdateSideBar(productId: number, routeName: string, typeChange: boolean): Promise<void> {
    // Fetch and transform the guide grid data
    const apiResponse = await this.getGuideGridDataList(productId);
    const transformedMenuData = this.transformMenuData(apiResponse);
    this.menuList = this.updatePropBasedOnRoute(transformedMenuData, routeName);
    AppSidebarComponent.latestMenuList = this.menuList;
    AppSidebarComponent.getMenuList.emit(this.menuList);
    if (!typeChange) {
      // Get the current route and determine navigation
      const currentRoute = this.router.url;
      const hasProductTypeEnum = currentRoute.includes(productId.toString());
      const hasBookMarkUniqueId = this.hasBookMarkUniqueId(this.menuList, currentRoute);
      if (hasProductTypeEnum && hasBookMarkUniqueId) {
        await this.router.navigateByUrl(currentRoute);
      } else if (hasProductTypeEnum) {
        const bookMarkUrl = this.getFirstBookMarkUrl(this.menuList);
        await this.router.navigateByUrl(`${routeName}/${bookMarkUrl}`);
      } else {
        await this.router.navigateByUrl(routeName);
      }
    } else {
      const bookMarkUrl = this.getFirstBookMarkUrl(this.menuList);
      await this.router.navigateByUrl(`${routeName}/${bookMarkUrl}`);
    }
  }

  async getGuideGridDataList(productId: number): Promise<any> {
    return new Promise((resolve) => {
      this._docService.GetInternalDocumentationLeftMenu(productId).then(response => {
        if (response && response.data) {
          this.menuList = [
            {
              "label": "",
              "child": response.data
            }
          ]
          resolve(this.menuList)
        }
      });
    })
  }

  transformMenuData(data: any[]): IMenu[] {
    return data.map(item => {
      // Map mainTitle to label for the parent item
      item.label = item.mainTitle;
      if (item.child && item.child.length > 0) {
        // Recursively transform children
        item.items = this.transformMenuData(item.child);
      }
      return item;
    });
  }

  updatePropBasedOnRoute(menuList: IMenu[], routeName: string): IMenu[] {
    menuList = menuList.length ? menuList : [];
    menuList[0].child?.forEach((menuItem: IMenu) => {
      menuItem.label = menuItem.mainTitle;
      menuItem.routerLink = ['/' + routeName + '/' + menuItem.bookMarkUniqueId];
      if (menuItem.child && menuItem.child.length) {
        menuItem.child.forEach((childItem: IMenuItem) => {
          childItem.label = childItem.mainTitle;
          childItem.routerLink = ['/' + routeName + '/' + childItem.bookMarkUniqueId];
        });
      }
    });
    return menuList;
  }

  resetOverlay() {
    if (this.layoutService.state.overlayMenuActive) {
      this.layoutService.state.overlayMenuActive = false;
    }
  }

  onMouseEnter() {
    if (!this.layoutService.state.anchored) {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }
      this.layoutService.state.sidebarActive = true;
    }
  }

  onMouseLeave() {
    if (!this.layoutService.state.anchored) {
      if (!this.timeout) {
        this.timeout = setTimeout(
          () => (this.layoutService.state.sidebarActive = false),
          300
        );
      }
    }
  }

  anchor() {
    this.layoutService.state.anchored = !this.layoutService.state.anchored;
  }

  ngOnDestroy() {
    this.resetOverlay();
  }

  private getRouteNameAndProductEnum(currentRoute: string): { routeName: string; productEnum: number | undefined } {
    if (!currentRoute.includes('/documentations-portal/internal/view')) {
      return {routeName: '', productEnum: undefined};
    }

    if (currentRoute.includes('/1')) {
      return {
        routeName: `documentations-portal/internal/view/${InternalProductEnum.InternalAnnualProcedures}`,
        productEnum: InternalProductEnum.InternalAnnualProcedures
      };
    } else if (currentRoute.includes('/2')) {
      return {
        routeName: `documentations-portal/internal/view/${InternalProductEnum.InternalSolutions}`,
        productEnum: InternalProductEnum.InternalSolutions
      };
    } else {
      return {
        routeName: `documentations-portal/internal/view/${InternalProductEnum.InternalAnnualProcedures}`,
        productEnum: InternalProductEnum.InternalAnnualProcedures
      };
    }
  }

  private getFirstBookMarkUrl(menuList: any[]): string {
    if (!menuList.length) return '';
    return menuList[0].child?.[0]?.bookMarkUniqueId || '';
  }

  private hasBookMarkUniqueId(menuList: any[], currentRoute: string): boolean {
    for (const menu of menuList) {
      if (menu.bookMarkUniqueId && currentRoute.includes(menu.bookMarkUniqueId)) {
        return true;
      }
      if (menu.child && this.hasBookMarkUniqueId(menu.child, currentRoute)) {
        return true;
      }
    }
    return false;
  }
}
